/**
 * TravelDossierInline — in-page dossier section with per-heading expansion.
 *
 * Shares the same generate/poll logic as TravelDossierPanel (`useDossier`)
 * but renders the content flush with the surrounding page. The dossier is
 * generated as a single markdown document that always uses level-2 headings
 * for its required sections (Visa, Weather, Currency, ...), so we split on
 * those headings and give each section its own collapsible block. The whole
 * dossier block is itself collapsible via the parent wrapper.
 *
 * See services/api-service/src/services/travel-dossier-generator.ts for the
 * canonical section list — we don't hardcode it here; whatever the model
 * emitted is what we render.
 */

import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { AlertCircle, ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDossier } from '../hooks/useDossier';
import { useCollapsibleSet } from './TravelSummaryCollapse';
import { sanitizeSourceUrl } from './TravelHelpers';

interface DossierSection {
  heading: string;
  body: string;
}

/**
 * Split a dossier markdown blob on level-2 headings. If the model somehow
 * emits content before the first `## ` (preamble), we bucket it under
 * "Overview" so nothing is lost on screen.
 */
function splitDossierSections(markdown: string): DossierSection[] {
  const lines = markdown.split(/\r?\n/);
  const sections: DossierSection[] = [];
  let current: DossierSection | null = null;
  const preamble: string[] = [];

  for (const line of lines) {
    const h2 = line.match(/^##\s+(.*\S)\s*$/);
    if (h2) {
      if (current) sections.push(current);
      current = { heading: h2[1], body: '' };
      continue;
    }
    if (current) {
      current.body += (current.body ? '\n' : '') + line;
    } else {
      preamble.push(line);
    }
  }
  if (current) sections.push(current);

  const preambleText = preamble.join('\n').trim();
  if (preambleText) {
    sections.unshift({ heading: 'Overview', body: preambleText });
  }
  return sections;
}

interface TravelDossierInlineProps {
  tripId: string;
}

export function TravelDossierInline({ tripId }: TravelDossierInlineProps) {
  const { dossier, loading, error, isPending, isGenerating, generate, featureEnabled } =
    useDossier(tripId);

  const sections = useMemo<DossierSection[]>(() => {
    if (!dossier?.markdown) return [];
    return splitDossierSections(dossier.markdown);
  }, [dossier?.markdown]);

  // Default-collapsed section set. Users can flip them open one by one.
  const { isOpen, toggle } = useCollapsibleSet(
    sections.map((s) => s.heading),
    /* defaultOpen */ false,
  );

  const generatedAt = dossier?.createdAt
    ? new Date(dossier.createdAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  if (!featureEnabled) {
    return (
      <div className="text-xs text-muted-foreground">
        The dossier feature is disabled. Re-enable it from Settings → Dossier to generate a
        destination brief.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {dossier ? `Last generated ${generatedAt}` : 'No dossier yet for this trip.'}
        </div>
        <Button size="sm" onClick={() => void generate()} disabled={isPending || isGenerating}>
          {isPending ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Working…
            </>
          ) : (
            <>
              <RefreshCw className="h-3 w-3 mr-1" /> {dossier ? 'Regenerate' : 'Generate'}
            </>
          )}
        </Button>
      </div>

      {loading && !dossier && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking for an existing dossier…
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Could not load dossier: {error}</span>
        </div>
      )}

      {isPending && (
        <div className="rounded-md border border-border p-3 bg-muted/50">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Loader2 className="h-4 w-4 animate-spin" /> Researching your destinations…
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            This takes 20–40 seconds because the assistant runs real web searches for each section.
          </p>
        </div>
      )}

      {dossier?.status === 'error' && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">Dossier generation failed.</div>
              {dossier.errorMessage && (
                <div className="text-xs text-muted-foreground mt-1">{dossier.errorMessage}</div>
              )}
              <div className="text-xs text-muted-foreground mt-2">Tap Regenerate to try again.</div>
            </div>
          </div>
        </div>
      )}

      {dossier?.status === 'ready' && sections.length > 0 && (
        <div className="space-y-1">
          {sections.map((section) => {
            const open = isOpen(section.heading);
            const Chevron = open ? ChevronDown : ChevronRight;
            return (
              <div key={section.heading} className="rounded border border-border">
                <button
                  type="button"
                  onClick={() => toggle(section.heading)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium hover:bg-muted/50"
                  aria-expanded={open}
                >
                  <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1">{section.heading}</span>
                </button>
                {open && (
                  <div className="px-3 pb-3 pt-1 prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{section.body}</ReactMarkdown>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {dossier?.status === 'ready' && dossier.sources.length > 0 && (
        <div className="border-t border-border pt-3">
          <div className="text-xs font-semibold mb-1.5 uppercase tracking-wide text-muted-foreground">
            Sources
          </div>
          <ol className="space-y-1 text-xs list-decimal list-inside">
            {dossier.sources
              .map((src) => ({ safeUrl: sanitizeSourceUrl(src.url), title: src.title }))
              .filter(
                (src): src is { safeUrl: string; title: string | undefined } =>
                  src.safeUrl !== null,
              )
              .map((src, i) => (
                <li key={`${src.safeUrl}-${i}`} className="break-all">
                  <a
                    href={src.safeUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-primary hover:underline"
                  >
                    {src.title || src.safeUrl}
                  </a>
                </li>
              ))}
          </ol>
        </div>
      )}
    </div>
  );
}
